/**
 * InfinitSales - Transcription Limit Modal
 * Shown when the user hits the monthly transcription quota.
 *
 * Props:
 *   isOpen: boolean
 *   onClose: () => void
 *   currentCount: number
 *   maxAllowed: number
 *   tierName: string        (e.g. "Free", "Starter")
 *   requiredTier: string    (e.g. "sales_pro")
 */
import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Mic, Sparkles, Loader2, AlertTriangle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import subscriptionService from '../services/global/subscriptionService';

const NEXT_TIER_LIMITS = {
  sales_starter: 20,
  sales_pro: 30,
  sales_business: 50,
};

const TIER_DISPLAY_NAMES = {
  sales_free: 'Free',
  sales_starter: 'Starter',
  sales_pro: 'Pro',
  sales_business: 'Business',
};

const TranscriptionLimitModal = ({
  isOpen,
  onClose,
  currentCount = 0,
  maxAllowed = 5,
  tierName = 'Free',
  requiredTier = 'sales_pro',
}) => {
  const { colors } = useTheme();
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [suggestedPlan, setSuggestedPlan] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  useEffect(() => {
    if (!isOpen || !requiredTier) return;
    let cancelled = false;
    (async () => {
      setLoadingPlans(true);
      try {
        const result = await subscriptionService.getPlans();
        const plans = result?.data || result?.plans || result || [];
        const match = Array.isArray(plans)
          // requiredTier e o id do catalogo (ex.: 'sales_pro'); o tier do catalogo e so 'pro'.
          ? plans.find((p) => p.id === requiredTier || p.slug === requiredTier || p.tier === requiredTier)
          : null;
        if (!cancelled) setSuggestedPlan(match || null);
      } catch (err) {
        console.error('[TranscriptionLimitModal] Could not load plans:', err);
      } finally {
        if (!cancelled) setLoadingPlans(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, requiredTier]);

  const handleUpgrade = async () => {
    if (!suggestedPlan?.id) return;
    setCheckoutLoading(true);
    try {
      const result = await subscriptionService.getCheckoutUrl(suggestedPlan.id);
      const url = result?.data?.checkout_url || result?.checkout_url;
      if (url) window.location.href = url;
    } catch (err) {
      console.error('[TranscriptionLimitModal] Checkout error:', err);
      setCheckoutLoading(false);
    }
  };

  if (!isOpen) return null;

  const requiredTierName = TIER_DISPLAY_NAMES[requiredTier] || 'Pro';
  const nextLimit = NEXT_TIER_LIMITS[requiredTier] || 30;

  const content = (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10050,
        padding: 20,
      }}
    >
      <div
        style={{
          backgroundColor: colors.bgPrimary,
          borderRadius: 16,
          width: '100%',
          maxWidth: 460,
          padding: '28px 26px',
          position: 'relative',
          boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
          border: `1px solid ${colors.borderColor}`,
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 34,
            height: 34,
            borderRadius: 8,
            border: 'none',
            background: 'transparent',
            color: colors.textTertiary,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          aria-label="Fechar"
        >
          <X size={18} />
        </button>

        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 14,
            background: 'rgba(239, 68, 68, 0.15)',
            color: '#DC2626',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16,
          }}
        >
          <AlertTriangle size={28} />
        </div>

        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: colors.textPrimary }}>
          Limite de transcrições atingido
        </h3>
        <p style={{ margin: '8px 0 18px', fontSize: 14, color: colors.textSecondary, lineHeight: 1.5 }}>
          Você usou <strong>{currentCount}/{maxAllowed}</strong> transcrições do plano{' '}
          <strong>{tierName}</strong> neste mês. A quota renova automaticamente no dia 1º do próximo mês.
        </p>

        <div
          style={{
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.borderColor}`,
            borderRadius: 12,
            padding: 16,
            marginBottom: 18,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <Sparkles size={18} color={colors.accentPrimary} />
            <strong style={{ color: colors.textPrimary, fontSize: 14 }}>
              Faça upgrade para o plano {requiredTierName}
            </strong>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: colors.textSecondary, fontSize: 13 }}>
            <Mic size={14} />
            <span>Até <strong>{nextLimit} transcrições por mês</strong> no plano {requiredTierName}</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              border: 'none',
              background: 'transparent',
              color: colors.textSecondary,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Agora não
          </button>
          <button
            onClick={handleUpgrade}
            disabled={loadingPlans || checkoutLoading || !suggestedPlan}
            style={{
              padding: '10px 20px',
              borderRadius: 8,
              border: 'none',
              background: colors.accentPrimary,
              color: 'white',
              fontSize: 14,
              fontWeight: 600,
              cursor: (loadingPlans || checkoutLoading || !suggestedPlan) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              opacity: (loadingPlans || checkoutLoading || !suggestedPlan) ? 0.7 : 1,
            }}
          >
            {(loadingPlans || checkoutLoading) ? (
              <>
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                Carregando...
              </>
            ) : (
              <>
                <Sparkles size={16} />
                Fazer upgrade
              </>
            )}
          </button>
        </div>

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default TranscriptionLimitModal;
